import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { Trash2, Minus, Plus, ArrowRight, ShoppingBag } from "lucide-react";

export default function Cart() {
    const { items, updateQty, remove, subtotal } = useCart();

    if (!items.length) {
        return (
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-24 text-center" data-testid="cart-empty">
                <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h1 className="font-display text-3xl font-bold mb-3">Your cart is empty</h1>
                <p className="text-muted-foreground mb-6">Browse our catalogue and add a few items to get started.</p>
                <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover transition-colors">
                    Shop products <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    const shipping = subtotal >= 100 ? 0 : 5;
    const total = subtotal + shipping;

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12" data-testid="cart-page">
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-10">Your cart</h1>
            <div className="grid md:grid-cols-[1fr_360px] gap-8">
                <div className="space-y-4">
                    {items.map((i) => (
                        <div key={i.product_id} className="flex gap-4 bg-white border border-border p-4" data-testid={`cart-item-${i.product_id}`}>
                            <Link to={`/products/${i.slug}`} className="w-24 h-24 bg-surface overflow-hidden flex-shrink-0">
                                {i.image_url && <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />}
                            </Link>
                            <div className="flex-1">
                                <Link to={`/products/${i.slug}`} className="font-display font-semibold hover:text-primary block">{i.name}</Link>
                                <div className="text-sm text-muted-foreground mb-2">{money(i.price)} each</div>
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center border border-border">
                                        <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="px-2 py-1 hover:bg-muted" data-testid={`cart-decrement-${i.product_id}`}><Minus className="w-3 h-3" /></button>
                                        <span className="px-3 text-sm font-semibold">{i.quantity}</span>
                                        <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="px-2 py-1 hover:bg-muted" data-testid={`cart-increment-${i.product_id}`}><Plus className="w-3 h-3" /></button>
                                    </div>
                                    <button onClick={() => remove(i.product_id)} className="text-muted-foreground hover:text-primary" data-testid={`cart-remove-${i.product_id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="font-display font-bold text-lg">{money(i.price * i.quantity)}</div>
                        </div>
                    ))}
                </div>

                <aside className="bg-surface p-6 h-fit border border-border" data-testid="cart-summary">
                    <h2 className="font-display font-bold text-xl mb-4">Order summary</h2>
                    <div className="space-y-2 text-sm border-b border-border pb-4 mb-4">
                        <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
                        <div className="flex justify-between"><span>Shipping {subtotal >= 100 && <span className="text-secondary text-xs">(free over $100)</span>}</span><span>{money(shipping)}</span></div>
                    </div>
                    <div className="flex justify-between font-display font-bold text-2xl mb-6">
                        <span>Total</span><span data-testid="cart-total">{money(total)}</span>
                    </div>
                    <Link
                        to="/checkout"
                        className="block w-full text-center bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover transition-colors"
                        data-testid="cart-checkout-btn"
                    >
                        Proceed to checkout
                    </Link>
                </aside>
            </div>
        </div>
    );
}
